import { useMemo } from "react";
import { Info } from "lucide-react";
import MoreInfoButton from "@/components/more-info-button";
import StyledTooltip from "@/components/styled-tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { useGetProviderBreakdown } from "@/hooks/use-inboxplacement";
import {
  b2bProviders,
  b2cProviders,
  EmailProvider,
  PlacementType,
  ProviderDetailedResult,
} from "@/types/inboxPlacements.types";
import ProviderCollapes, {
  ProviderCollapesSkeleton,
} from "../provider-collapes";
import { usePlacementContext } from "./placement-context";

// Constants
const TOOLTIP_DESCRIPTION =
  "Shows how your emails are delivered across different email service providers, categorized by B2C (consumer) and B2B (business) providers. This helps identify if deliverability issues are specific to certain provider types.";

// Utility functions
const mapDeliveryStatus = (deliveredTo: string | undefined): string => {
  if (!deliveredTo) return "Unreceived";

  switch (deliveredTo.toLowerCase()) {
    case "inbox":
      return "Inbox";
    case "spam":
      return "Spam";
    default:
      return "Unreceived";
  }
};

const createPriorityMap = (providersList: EmailProvider[]) => {
  return Object.fromEntries(
    providersList.map((provider, index) => [provider, index])
  );
};

const transformProviderData = (provider: ProviderDetailedResult) => {
  const stats = {
    inbox: Math.round(provider.inboxPercentage),
    spam: Math.round(provider.spamPercentage),
    unreceived: Math.round(provider.unreceivedPercentage),
  };

  const category = provider.categoryBreakdown
    ? Object.keys(provider.categoryBreakdown).filter(
        (key) =>
          key !== "other" &&
          (provider.categoryBreakdown?.[
            key as keyof typeof provider.categoryBreakdown
          ]?.count || 0) > 0
      )
    : [];

  const emails = provider.emails.map((email) => ({
    address: email.email,
    status: mapDeliveryStatus(email.deliveredTo),
    category: email.category?.toLowerCase() || undefined,
    authStatus: email.authStatus,
    senderIp: email.senderIP,
  }));

  return {
    provider: provider.displayName,
    providerName: provider.provider,
    stats,
    category,
    emails,
  };
};

const PlacementProviderBreakdown = () => {
  const { state } = usePlacementContext();

  const { data: providerBreakdown, isLoading: isProviderBreakdownLoading } =
    useGetProviderBreakdown(state.params.email, state.params.testId);

  // Memoized computations
  const filteredAndSortedProviders = useMemo(() => {
    if (
      !providerBreakdown?.providers ||
      providerBreakdown.providers.length === 0
    ) {
      return [];
    }

    // Since we're using B2B API but showing all providers, combine both lists
    // B2B providers come first, then B2C providers
    const allProviders = [...b2bProviders, ...b2cProviders];
    const providersList =
      state.params.type === PlacementType.B2C ? b2cProviders : allProviders;

    const priorityMap = createPriorityMap(providersList);

    return providerBreakdown.providers
      .filter((provider) => {
        // For B2B API, include both B2B and B2C providers
        // For B2C type, only include B2C providers
        if (state.params.type === PlacementType.B2C) {
          return b2cProviders.includes(provider.provider as EmailProvider);
        } else {
          // Include both B2B and B2C providers for B2B API
          return (
            b2bProviders.includes(provider.provider as EmailProvider) ||
            b2cProviders.includes(provider.provider as EmailProvider)
          );
        }
      })
      .sort((a, b) => {
        const priorityA = priorityMap[a.provider as EmailProvider] ?? 999;
        const priorityB = priorityMap[b.provider as EmailProvider] ?? 999;
        return priorityA - priorityB;
      })
      .map(transformProviderData);
  }, [providerBreakdown?.providers, state.params.type]);

  const hasProviders = filteredAndSortedProviders.length > 0;

  return (
    <div className="space-y-2 mt-6 mb-6">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-medium">Placement by Provider Type</h2>
        <StyledTooltip description={TOOLTIP_DESCRIPTION}>
          <Info className="h-4 w-4 text-muted-foreground" />
        </StyledTooltip>
        <MoreInfoButton slug="email-placement-by-provider-type" />
      </div>

      <Card className="border-none bg-transparent">
        <CardContent className="p-0 space-y-3">
          {isProviderBreakdownLoading ? (
            <ProviderCollapesSkeleton />
          ) : hasProviders ? (
            filteredAndSortedProviders.map((transformedProvider, index) => (
              <ProviderCollapes
                key={`${transformedProvider.providerName}-${index}`}
                provider={transformedProvider.provider}
                stats={transformedProvider.stats}
                emails={transformedProvider.emails}
                category={transformedProvider.category}
                testId={state.params.testId}
                forceOpen={state.isPrinting}
                providerName={transformedProvider.providerName as EmailProvider}
              />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No provider data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlacementProviderBreakdown;
